import { useId } from 'react';
import { Button, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyTwoTone';
import CopyAllIcon from '@mui/icons-material/CopyAllTwoTone';
import { Field, Form, useFormikContext } from 'formik';
import { FormikTextInput } from '@forms/Components/TextInput';
import SubmitFormButton from '@forms/Components/SubmitFormButton';
import DisabledTooltip from '@components/DisabledTooltip';
import ColumnSelection from './ColumnSelection';
import FilterSelection from './FilterSelection';
import EditorCard from './EditorCard';

import styles from './Page.module.css';

/**
 * Editable configuration for the selected Board Query. Currently the query name and its column order —
 * column selection, filters, and sort order follow.
 *
 * A query being created is the one case where the name is editable, since a saved query's name is what the board
 * and the saved layouts resolve it by. Copying is what a user reaches for instead: it opens the selected query's
 * configuration as a new one, under a name they can change.
 */
export default function QueryEditor(props: {
  isNewQuery: boolean;
  isActive: boolean;
  isUpdatingActiveState: boolean;
  onToggleActive: () => void;
  onCopy: () => void;
  /** Absent when the user has no other site to copy to, which hides the button */
  onCopyToSite?: () => void;
  onCancel: () => void;
}) {
  const formId = useId();
  const { dirty } = useFormikContext();

  return (
    <Form
      id={formId}
      className={styles.editor}>
      <div className={styles['editor-header']}>
        <Field
          name='name'
          label='Query Name'
          required
          component={FormikTextInput}
          className={styles['name-input']}
          disabled={!props.isNewQuery}
          disabledReason='Query name cannot be edited, use Copy to start a new query from this one'
        />

        {!props.isNewQuery && (
          <IconButton
            title='Copy to a New Board Query'
            onClick={props.onCopy}
            className={`${styles['icon-btn']} ${styles['copy-btn']}`}
          >
            <ContentCopyIcon />
          </IconButton>
        )}

        {!props.isNewQuery && props.onCopyToSite && (
          // The copy is taken from the saved query, so pending edits are held back until they are saved
          <DisabledTooltip message={dirty ? 'Save your changes before copying to another site' : undefined}>
            <IconButton
              title='Copy to Another Site'
              disabled={dirty}
              onClick={props.onCopyToSite}
              className={`${styles['icon-btn']} ${styles['copy-btn']}`}
            >
              <CopyAllIcon />
            </IconButton>
          </DisabledTooltip>
        )}

        {!props.isNewQuery && !props.isActive && <span className={styles['disabled-badge']}>Disabled</span>}

        <div className={styles['editor-actions']}>
          {!props.isNewQuery && (
            <Button
              variant='outlined'
              color={props.isActive ? 'error' : 'primary'}
              loading={props.isUpdatingActiveState}
              title={props.isActive ? 'Remove this query from the Dispatch Board' : 'Return this query to the Dispatch Board'}
              onClick={props.onToggleActive}
            >
              {props.isActive ? 'Disable' : 'Enable'}
            </Button>
          )}

          {props.isNewQuery && (
            <Button
              variant='outlined'
              onClick={props.onCancel}
            >
              Cancel
            </Button>
          )}

          <SubmitFormButton
            formName={formId}
            // A copy arrives complete, so it is savable before the user has changed anything about it
            enableSubmitAlways={props.isNewQuery}
            submitButtonText={props.isNewQuery ? 'Create' : 'Save'}
            submitButtonMessage={props.isNewQuery ? 'Create Query' : 'Save Changes'}
          />
        </div>
      </div>

      <div className={styles['editor-fields']}>
        <EditorCard title='Columns'>
          <ColumnSelection />
        </EditorCard>

        <EditorCard title='Filters'>
          <FilterSelection />
        </EditorCard>
      </div>
    </Form>
  );
}
